import React, { FC, useEffect, useMemo } from "react";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import * as Yup from "yup";
import { Box, CircularProgress, Typography, Divider, MenuItem } from "@mui/material";
import { CustomButton, CustomTextBox } from "../../atoms";
import CustomDateTimePicker from "../../atoms/custom-date-time-picker/custom-date-time-picker";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import moment from "moment-timezone";
import { getCustomerConfig, updateCustomerConfig } from "../../../redux/actions/customerConfigAction";
import { customerConfigActions } from "../../../redux/slice/customerConfigSlice";

interface ICustomerSettingsDrawerProps {
    open: boolean;
    toggleDrawer: () => void;
}

const schema = Yup.object().shape({
    cronTime: Yup.string().required("Run time is required"),
    inactivityWindowDays: Yup.number()
        .required("Inactivity window is required")
        .min(1, "Must be at least 1 day"),
    timeZone: Yup.string().required("Timezone is required"),
});

type FormData = Yup.InferType<typeof schema>;
const resolver: Resolver<FormData> = yupResolver(schema);

const CustomerSettingsDrawer: FC<ICustomerSettingsDrawerProps> = ({
    open,
    toggleDrawer,
}) => {
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormData>({
        resolver: resolver,
        defaultValues: {
            cronTime: "00:00",
            inactivityWindowDays: 60,
            timeZone: "America/New_York",
        },
    });

    const dispatch = useAppDispatch();
    const { config, loading, updateSuccess } = useAppSelector(
        (state) => state.customerConfig
    );
    const { showSnackbar } = useSnackbarContext();
    const timezoneOptions = useMemo(() => moment.tz.names(), []);

    useEffect(() => {
        if (open) {
            dispatch(getCustomerConfig());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (config) {
            // Convert cronExpression "0 0 0 * * ?" to time "00:00"
            // Assumes format "s m H * * ?"
            let time = "00:00";
            if (config.cronExpression) {
                const parts = config.cronExpression.split(" ");
                if (parts.length >= 3) {
                    const h = parts[2].padStart(2, "0");
                    const m = parts[1].padStart(2, "0");
                    time = `${h}:${m}`;
                }
            }
            setValue("cronTime", time);
            setValue("inactivityWindowDays", config.inactivityWindowDays);
            setValue("timeZone", config.timeZone);
        }
    }, [config, setValue]);

    useEffect(() => {
        if (updateSuccess) {
            showSnackbar("Configuration updated successfully", "success");
            dispatch(customerConfigActions.resetUpdateSuccess());
            toggleDrawer();
        }
    }, [updateSuccess, showSnackbar, dispatch, toggleDrawer]);

    const handleFormSubmit = (data: FormData) => {
        // Convert time "HH:mm" back to cron "0 m H * * ?"
        const [h, m] = data.cronTime.split(":");
        const cronExpression = `0 ${Number(m)} ${Number(h)} * * ?`;

        dispatch(
            updateCustomerConfig({
                cronExpression,
                inactivityWindowDays: data.inactivityWindowDays,
                timeZone: data.timeZone,
            })
        );
    };

    return (
        <CustomDrawer
            isOpen={open}
            toggleDrawer={toggleDrawer}
            sx={{ width: "30rem" }}
        >
            <form
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2rem",
                    padding: "2rem",
                }}
                onSubmit={handleSubmit(handleFormSubmit)}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Customer Automation Settings
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Configure automated customer deactivation settings.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <Box sx={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body1">
                            Deactivation Job Run Time:
                        </Typography>
                        <Controller
                            name="cronTime"
                            control={control}
                            render={({ field }) => (
                                <CustomDateTimePicker
                                    type="time"
                                    value={field.value ? dayjs(field.value, "HH:mm") : null}
                                    onChange={(date) =>
                                        field.onChange(date ? date.format("HH:mm") : "")
                                    }
                                    sx={{ width: "12rem" }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body1">
                            Time Zone:
                        </Typography>
                        <Controller
                            name="timeZone"
                            control={control}
                            render={({ field }) => (
                                <CustomTextBox
                                    select
                                    value={field.value}
                                    onChange={field.onChange}
                                    sx={{ width: "12rem" }}
                                >
                                    {timezoneOptions.map((tz) => (
                                        <MenuItem key={tz} value={tz}>
                                            {tz}
                                        </MenuItem>
                                    ))}
                                </CustomTextBox>
                            )}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body1">
                            Inactive After (Days):
                        </Typography>
                        <Controller
                            name="inactivityWindowDays"
                            control={control}
                            render={({ field }) => (
                                <CustomTextBox
                                    type="number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    sx={{ width: "12rem" }}
                                    inputProps={{ min: 1 }}
                                />
                            )}
                        />
                    </Box>
                </Box>

                <CustomButton
                    sx={{ fontSize: "1rem", paddingBlock: "0.5rem" }}
                    type="submit"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : "Save Configuration"}
                </CustomButton>
            </form>
        </CustomDrawer>
    );
};

export { CustomerSettingsDrawer };
